import React, { useState, useCallback, useEffect, useRef } from 'react';
import { api } from './api';
import { captureMetaAttribution } from './utils/metaAttribution';
import { captureReferralCode } from './utils/referralAttribution';
import { captureFunnelAttribution, captureUtmSource, captureAdClickPlatform } from './utils/funnelAttribution';
import Step01Welcome from './steps/Step01Welcome';
import Step02PersonalInfo from './steps/Step02PersonalInfo';
import Step03City from './steps/Step03City';
import Step04Country from './steps/Step04Country';
import StepValueProp from './steps/StepValueProp';
import Step05LinkedInCredentials from './steps/Step05LinkedInCredentials';
import Step06AutomatedLogin from './steps/Step06AutomatedLogin';
import Step07TwoFA from './steps/Step07TwoFA';
import Step09IDBadgeUpsell from './steps/Step09IDBadgeUpsell';
import Step10SelfieUpload from './steps/Step10SelfieUpload';
import Step11IDType from './steps/Step11IDType';
import Step12IDBadgeVerification from './steps/Step12IDBadgeVerification';
import Step13Consent from './steps/Step13Consent';
import Step15ReferralSource from './steps/Step15ReferralSource';
import Step16ReferrerName from './steps/Step16ReferrerName';
import Step17EmailChange2FA from './steps/Step17EmailChange2FA';
import Step18Finalizing from './steps/Step18Finalizing';
import Step19SessionCleanup from './steps/Step19SessionCleanup';
import Step20FinalDetails from './steps/Step20FinalDetails';
import Step21PaymentDetails from './steps/Step21PaymentDetails';
import StepMessageYourAgent from './steps/StepMessageYourAgent';
import Step22Confirmation from './steps/Step22Confirmation';
import EarningsProgressTracker from './components/EarningsProgressTracker';
import TakeoverNav from './components/TakeoverNav';
import ApproveOnPhone from './components/ApproveOnPhone';
import GenericChallenge from './components/GenericChallenge';
import StepCaptchaViewer from './components/StepCaptchaViewer';
import {
  EmailChangeRestartScreen,
  RecoveringScreen,
  SellerRetryScreen,
  OperatorReviewScreen,
  TerminalFailedScreen,
  PasswordResetFailedScreen,
  AccountTooYoungScreen,
  TakeoverErrorBoundary,
} from './components/StateScreens';
import useInjection from './hooks/useInjection';
import useSessionState from './hooks/useSessionState';
import useReplayRecorder from './hooks/useReplayRecorder';
import { FINAL_DETAILS_STEP_NUMBER, resolveEmailChangeResumeStep } from './utils/emailChangeResume';
import { consumeHandoffParam, clearHandoffPending } from './utils/chromeHandoff';
import { shouldShowEarningsTracker } from './utils/earningsProgress';
import {
  isEmailChangeBrowserRestartState,
  shouldShowResetInboxNudge,
  isResetCodeEntryStatus,
  readResetCodePromptSeen,
  persistResetCodePromptSeen,
} from './utils/recoveryState';
import {
  ID_BADGE_ENABLED,
  ID_VERIFICATION_AUTOMATED,
  VERIFICATION_ENVELOPE_ENABLED,
  RELEASE_ON_FAILURE_ENABLED,
  RESET_CODE_INBOX_NUDGE_ENABLED,
  SNAP_CHROME_HANDOFF_ENABLED,
} from './utils/featureFlags';
import {
  classifySessionDisposition,
  clearPersistedSession,
} from './utils/sessionDisposition';

// iter-26: STEPS reorder per spec:
//   - Step08 LoginConfirmation DELETED (operator: "doesn't add anything")
//   - Selfie + ID Type moved BEFORE Credentials so we capture identity
//     intent up front (operator note: "selfie should come after the
//     REQUIREMENTS page")
//   - ID Badge Upsell (Step09) is now reached after login and is
//     conditional on the user having ticked Passport on ID Type (since
//     LinkedIn requires a passport for the verified badge)
//
// Final 1-indexed order:
//   1  Welcome
//   2  PersonalInfo
//   3  Country
//   4  City
//   5  ValueProp (Requirements)
//   6  SelfieUpload
//   7  IDType
//   8  LinkedInCredentials
//   9  AutomatedLogin
//   10 TwoFA               (injection-only)
//   11 IDBadgeUpsell       (skip if no Passport, or already-verified)
//   12 IDBadgeVerification — "Waiting for you" page (skip if upsell !== Yes)
//   13 Consent
//   14 ReferralSource
//   15 ReferrerName        (skip if Facebook)
//   16 EmailChange2FA      (injection-only)
//   17 Finalizing          (skip if emailChangeComplete)
//   18 SessionCleanup
//   19 FinalDetails
//   20 PaymentDetails
//   21 MessageYourAgent    (skips itself when no FB agent is available)
//   22 Confirmation
const STEPS = [
  Step01Welcome, Step02PersonalInfo, Step04Country, Step03City,
  StepValueProp,
  Step10SelfieUpload, Step11IDType,
  Step05LinkedInCredentials, Step06AutomatedLogin, Step07TwoFA,
  Step09IDBadgeUpsell, Step12IDBadgeVerification,
  Step13Consent,
  Step15ReferralSource, Step16ReferrerName,
  Step17EmailChange2FA, Step18Finalizing, Step19SessionCleanup,
  Step20FinalDetails, Step21PaymentDetails, StepMessageYourAgent, Step22Confirmation,
];

// Operator-visibility view names per directive B. Maps the 22 STEPS
// indices to the 13-name semantic vocabulary the operator uses to
// reason about "what cluster of screens is the seller actually on".
// Many pre-LinkedIn data-capture screens collapse to `personal_info`
// — coarse on purpose, since the operator cares about whether the FE
// is stuck on a generic data-entry view vs. one of the network-bound
// stages (login dispatch, 2FA wait, email-change, payment). Failure
// + completed are end-states surfaced from the BE status, not step
// index. Length MUST equal STEPS.length; CRA dev build will warn if
// a step is added without updating this map.
const STEP_VIEW_MAP = [
  'intro',                       // 0  Step01Welcome
  'personal_info',               // 1  Step02PersonalInfo
  'personal_info',               // 2  Step04Country
  'personal_info',               // 3  Step03City
  'personal_info',               // 4  StepValueProp
  'personal_info',               // 5  Step10SelfieUpload
  'personal_info',               // 6  Step11IDType
  'linkedin_creds',              // 7  Step05LinkedInCredentials
  'automated_login_dispatched',  // 8  Step06AutomatedLogin
  'awaiting_2fa',                // 9  Step07TwoFA  (app_approval sub-state TODO follow-up)
  'personal_info',               // 10 Step09IDBadgeUpsell
  'personal_info',               // 11 Step12IDBadgeVerification
  'personal_info',               // 12 Step13Consent
  'personal_info',               // 13 Step15ReferralSource
  'personal_info',               // 14 Step16ReferrerName
  'awaiting_email_code',         // 15 Step17EmailChange2FA
  'email_change_running',        // 16 Step18Finalizing
  'email_change_running',        // 17 Step19SessionCleanup
  'payment_pending',             // 18 Step20FinalDetails
  'payment_pending',             // 19 Step21PaymentDetails
  'payment_pending',             // 20 StepMessageYourAgent (not 'completed': the replay keeps recording until Confirmation)
  'completed',                   // 21 Step22Confirmation
];

// In dev mode only, allow jumping to a step via ?step=N (1-indexed) so the
// design can be reviewed without going through the backend. No effect in
// production builds.
function getInitialStep() {
  if (process.env.NODE_ENV !== 'development') return 0;
  if (typeof window === 'undefined') return 0;
  const raw = new URLSearchParams(window.location.search).get('step');
  if (!raw) return 0;
  const idx = parseInt(raw, 10) - 1;
  if (Number.isNaN(idx) || idx < 0 || idx > STEPS.length - 1) return 0;
  return idx;
}

// Optional `?country=us|gb` ad-landing param. US-ad URLs can carry `?country=us` so
// the seller starts on the right country BEFORE the phone step (the phone widget
// default, the earnings currency, and the Step-4 country selector all read
// sessionData.country). UK-safe: ONLY an explicit us/gb value is honoured — any
// other or absent value leaves the existing behaviour (locale heuristic → GB
// fallback) completely untouched, so UK-ad traffic is unaffected.
function getInitialCountryParam() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = (new URLSearchParams(window.location.search).get('country') || '').trim().toUpperCase();
    const alias = raw === 'UK' ? 'GB' : raw;
    return (alias === 'US' || alias === 'GB') ? alias : null;
  } catch {
    return null;
  }
}

const API_URL = process.env.REACT_APP_TAKEOVER_API_URL || 'http://localhost:8000';

// B-VERIFICATION-OVERLAY (2026-05-29): durable fix for "seller never sees
// the verification prompt". When ENABLED (default), every challenge
// (code / two_factor / app_approval / captcha) is surfaced level-driven via
// the /state envelope + GenericChallenge (useSessionState already polls it).
// The legacy edge-triggered useInjection step-jump (Step07/Step17) and the
// separate globalApprovalOverlay are RETIRED to avoid a second, missable
// (and double-prompting) surface. Kill switch: set
// REACT_APP_DISABLE_VERIFICATION_ENVELOPE=1 to restore the legacy path
// verbatim (instant rollback, no code revert). See
// docs/specs/verification-input-overlay.md.
// VERIFICATION_ENVELOPE_ENABLED now lives in utils/featureFlags.js (single
// source of truth, shared with Step06AutomatedLogin.jsx).

export default function App() {
  const [step, setStep] = useState(getInitialStep);
  const [savedStep, setSavedStep] = useState(null);
  const [sessionId, setSessionId] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem('takeover.sessionId') : null,
  );
  const [sessionToken, setSessionToken] = useState(() =>
    typeof window !== 'undefined' ? sessionStorage.getItem('sessionToken') : null,
  );
  const [sessionData, setSessionData] = useState(() => {
    // Seed a phone-only countryHint from the ?country=us|gb ad param (if present)
    // so the phone widget defaults to +1/+44. Deliberately NOT `country`: the
    // seller still explicitly picks their country on Step 4 (no pre-select /
    // auto-advance, no effect on proxy or currency), so a mis-tagged link can't
    // silently reroute a seller — it only nudges the phone dial code.
    const c = getInitialCountryParam();
    return c ? { countryHint: c } : {};
  });
  // B-AWAIT-APPROVAL-1 (2026-05-17): global app-approval overlay
  // driven by /status.awaiting_app_approval. The flag is DB-backed
  // (step_logs.metadata.awaiting_app_approval set by V3 driver) so a
  // seller who is mid-wizard when BE emits `needs_app_approval`
  // doesn't miss the prompt — useInjection's 3s poll catches the flag
  // regardless of which step is mounted, and this overlay sits on top
  // of every step until V3 detects the post-challenge redirect (or
  // times out) and clears the flag. Reference incident: Filip Test 1
  // retry 2026-05-17 — needs_app_approval fired during the form
  // filling window, Step22 mounted late, polished card never showed,
  // V3 hit app_approval_timeout. See
  // services/v3_driver.py:needs_app_approval block.
  const [globalApprovalOverlay, setGlobalApprovalOverlay] = useState(false);
  // B-CAPTCHA-FE-SURFACING (2026-05-30): when the envelope projects a captcha,
  // App renders the interactive StepCaptchaViewer (screencast solver). If the
  // screencast stream can't load (disabled / unauthorized / ws fail), fall back
  // to the passive GenericChallenge copy instead of leaving a broken canvas.
  // Keyed by the active challenge_id so a re-challenge re-arms the solver.
  const [captchaSolverFailedFor, setCaptchaSolverFailedFor] = useState(null);
  // B-CREDS-RECOVERY (2026-05-30): true from when the seller taps "Reset
  // password" until the recovery is visibly underway (envelope drives it), it
  // bounces back to wrong-creds, or the wizard advances. Bridges the ~10-15s
  // gap before the reset task opens the browser + sets password_reset_flow, so
  // the seller sees a "starting recovery" screen instead of Step06's stale
  // "Still not working — try resetting your LinkedIn password" escalation.
  const [recoveryStarting, setRecoveryStarting] = useState(false);
  // B-FE-FAILED-GATE (2026-05-17): when the BE flips
  // takeover_sessions.status to 'failed' (V3 task crash, B26 trap,
  // mark_session_failed, etc.), block forward-nav and render a
  // terminal error screen with a retry option. Without this gate,
  // sellers can advance Step02 → Step20 filling forms for a session
  // the BE already terminated — burning their time + producing
  // zombie inputs. Kill switch REACT_APP_DISABLE_FAILED_GATE=1
  // restores pre-gate behavior. Pair with PR #364 (Step06 retry
  // surfacing) — both terminators converge on the same UX.
  const [globalSessionFailed, setGlobalSessionFailed] = useState(null);
  // B-CREDS-FE-EXPLICIT R2/R3 (2026-05-29): structured wrong-credentials
  // signal from useInjection's /status poll. Drives the global overlay
  // (renders on top of any step) and the forward-nav gate in next() /
  // TakeoverNav.onForward.
  //   wrongCreds = null         → no wrong-creds state on the BE
  //   wrongCreds = { kind, message } when status='waiting_for_credentials_retry'
  //     (kind='recoverable') OR status='terminal_failed' AND terminalReason=
  //     'credential_retry_limit_exceeded' (kind='limit_exceeded').
  // Cleared by useInjection on transition back to in_progress (which my
  // PUT /credentials change drives on resubmit).
  const [wrongCreds, setWrongCreds] = useState(null);
  // B-CREDS-RECOVERY (2026-05-30): drop the "starting recovery" bridge when the
  // session bounces back to the wrong-creds overlay (overlay owns the screen)
  // or when the wizard advances (recovery succeeded → login done). While the
  // reset runs, `step` stays on the login step, so the bridge persists and
  // covers the gaps where the envelope isn't actively overriding.
  useEffect(() => { if (wrongCreds) setRecoveryStarting(false); }, [wrongCreds]);
  useEffect(() => { setRecoveryStarting(false); }, [step]);
  // Highest step index the seller has reached. Used to gate the
  // forward-arrow on TakeoverNav: forward is allowed only up to
  // maxReachedStep so a back-then-forward returns to where they came
  // from without skipping past un-filled pages.
  const [maxReachedStep, setMaxReachedStep] = useState(() => {
    if (typeof window === 'undefined') return 0;
    const raw = parseInt(localStorage.getItem('takeover.maxStep') || '0', 10);
    return Number.isFinite(raw) ? raw : 0;
  });
  // Hydration gate — true while we wait for the BE GET /sessions/:id
  // call after a page refresh. Blocks initial render so the seller
  // doesn't see Step01 flash before the resumed step takes over.
  const [hydrating, setHydrating] = useState(() =>
    typeof window !== 'undefined' &&
    !!localStorage.getItem('takeover.sessionId') &&
    !!sessionStorage.getItem('sessionToken'),
  );
  // SNAP-ANDROID-SELFIE-HANDOFF (2026-11-03): a seller sent here from
  // Snapchat's Android in-app browser carries their idempotency key in the
  // URL. consumeHandoffParam adopts it (and clears any stored sessionId, so
  // `sessionId`/`hydrating` above stay empty); the effect below rejoins the
  // SAME session through createSession's dedup path and resumes at the
  // selfie step.
  // Dormant until SNAP_CHROME_HANDOFF_ENABLED (default off): a ?handoff= link
  // is then left untouched and the wizard boots exactly as before.
  const [handoffPending, setHandoffPending] = useState(
    () => SNAP_CHROME_HANDOFF_ENABLED && consumeHandoffParam(),
  );

  // PR 4 — bumped on stale-challenge rejection to force useSessionState
  // to re-poll for the freshest envelope. Increment whenever a submit
  // surfaces a 409 / "stale challenge" response.
  const [statePollNonce, setStatePollNonce] = useState(0);
  const [emailChangeRestarting, setEmailChangeRestarting] = useState(false);
  const [emailChangeRestartError, setEmailChangeRestartError] = useState(null);
  // B-SELLER-RETRY (2026-06-02): in-flight flag for the seller-initiated
  // retry of a blocked leg (status='waiting_for_seller_retry'). Disables the
  // CTA + shows a "Restarting…" state while POST /retry is in flight.
  const [sellerRetrying, setSellerRetrying] = useState(false);

  // RESET-CODE-INBOX-NUDGE (2026-10-09, thread reset-code-wizard-nudge): latches
  // true once the reset code-entry prompt (waiting_for_code OR waiting_for_2fa —
  // an OTP-kind reset code projects as the latter) has been shown this session,
  // so the "watch your inbox" nudge on recovering_password only fires BEFORE the
  // code arrives. The BE re-projects recovering_password after the code is
  // submitted (awaiting_2fa clears, password_reset_flow lingers), so without this
  // latch the stale nudge would re-appear post-submit. Seeded from sessionStorage
  // so a reload during that post-code phase does not re-show it either.
  const [resetCodePromptSeen, setResetCodePromptSeen] = useState(() =>
    readResetCodePromptSeen(
      typeof window !== 'undefined' ? localStorage.getItem('takeover.sessionId') : null,
    ),
  );

  // Phase 4 Meta→GHL: capture lead_id / form_id / page_id / etc. from
  // URL params on first paint. Persists into sessionStorage so the
  // values survive page reloads + step navigations. Stays NULL for
  // organic / direct-traffic sessions, and that's fine — the
  // completion-webhook path falls back to email/phone matching.
  useEffect(() => {
    captureMetaAttribution();
    // Referral program (migration 095): capture ?ref=<code> and fire a
    // best-effort click beacon. Never blocks the page.
    const refCode = captureReferralCode();
    if (refCode) {
      api.referralClick(refCode).catch(() => {});
    }
    // Funnel attribution (migration 233): capture `?fs=<tag>` and the landing
    // domain on first paint, same durability class as the referral code. Must
    // run HERE and not at session-create, because the seller can land on
    // step 1 with the tag and only create a session after an in-app
    // navigation has dropped the query string.
    captureFunnelAttribution();
    // Ad-platform source and click id (migration 343), for the same reason.
    captureUtmSource();
    captureAdClickPlatform();
  }, []);

  const StepComponent = STEPS[step];

  // iter-26 skip rules (1-indexed step numbers):
  //   step 10 — TwoFA: injection-only target, never reached via next()
  //   step 11 — IDBadgeUpsell: skip if user has the badge already, OR if
  //             they did NOT tick Passport on IDType (LinkedIn's verified
  //             badge requires a passport — operator spec)
  //   step 12 — IDBadgeVerification (Waiting for you): skip if the upsell
  //             screen was skipped OR the user picked "No thanks, skip"
  //   step 15 — ReferrerName: ALWAYS skipped (B-LINK-ONLY 2026-06-03) — the
  //             typed-name referral question is retired; payouts are link-only
  //             so a seller-typed name is no longer collected
  //   step 16 — EmailChange2FA: injection-only target
  //   step 17 — Finalizing: skip if email change already complete by the
  //             time the seller arrives (V3 may have completed inline)
  const shouldSkip = useCallback((idx, data) => {
    const stepNumber = idx + 1;
    if (stepNumber === 10) return true;
    if (stepNumber === 11) {
      // B-IDBADGE-TOGGLE (2026-05-29): when the ID-badge sub-flow is
      // disabled, the upsell never renders — flow goes straight from
      // login to consent. Email-change still dispatches (it auto-chains
      // server-side on V3 login success, independent of this step), and
      // Step13 fires a belt-and-suspenders identity_confirm for the
      // legacy non-V3 path. See utils/featureFlags.js.
      if (!ID_BADGE_ENABLED) return true;
      if (data.verifiedBadgePresent === true) return true;
      const idTypes = Array.isArray(data.idTypes) ? data.idTypes : [];
      // B-IDBADGE-V2 (2026-06-22): the automated flow handles BOTH Persona
      // (GB/passport) and CLEAR (US/driver's license), so it's offered when the
      // seller ticked ANY government ID — not passport-only (the legacy Persona
      // heuristic). Region-specific availability degrades gracefully at runtime
      // (initiate soft-fails to a seller-safe message if no QR is reachable).
      if (ID_VERIFICATION_AUTOMATED ? idTypes.length === 0 : !idTypes.includes('Passport')) return true;
      return false;
    }
    if (stepNumber === 12) {
      if (!ID_BADGE_ENABLED) return true; // B-IDBADGE-TOGGLE
      if (data.verifiedBadgePresent === true) return true;
      const idTypes = Array.isArray(data.idTypes) ? data.idTypes : [];
      if (ID_VERIFICATION_AUTOMATED ? idTypes.length === 0 : !idTypes.includes('Passport')) return true;
      if (data.idBadgeUpsell !== 'Yes') return true;
      return false;
    }
    if (stepNumber === 15) {
      // B-LINK-ONLY (2026-06-03): the typed-name "Who referred you?" question
      // (Step16ReferrerName) is retired — referral payouts are link-only, so a
      // seller-typed referrer name is no longer collected. ALWAYS skip it (was:
      // skip only for Facebook-ad sellers). Kept as an always-skip (NOT spliced
      // out of the STEPS array) so every step index / goToStep target / earnings
      // progress threshold / STEP_VIEW_MAP entry / BE current_step mapping stays
      // byte-identical. The ?ref LINK channel (captureReferralCode) is untouched.
      return true;
    }
    if (stepNumber === 16) return true;
    if (stepNumber === 17) return data.emailChangeComplete === true;
    return false;
  }, []);

  const next = useCallback(() => {
    // B-FE-FAILED-GATE (2026-05-17): if the BE has flipped the session
    // to 'failed', refuse to advance. The terminal overlay will be
    // rendered above the current step; seller can use its Retry button
    // (which goes back to credentials) or close the tab. Without this
    // gate, sellers walk Step02 → Step20 of a session the BE already
    // terminated.
    if (globalSessionFailed) {
      // eslint-disable-next-line no-console
      console.log('[B-FE-FAILED-GATE] next() refused — session is failed');
      return;
    }
    // B-CREDS-FE-EXPLICIT R2 (2026-05-29): refuse to advance while the
    // BE is in a wrong-credentials state. Pairs with the global overlay
    // (R3) — the overlay is the visible "fix this first" surface; this
    // gate prevents programmatic next() calls (Enter-to-submit, etc.)
    // from walking past it.
    if (wrongCreds) {
      // eslint-disable-next-line no-console
      console.log('[B-CREDS-FE-EXPLICIT] next() refused — wrong-credentials active', wrongCreds);
      return;
    }
    setStep((s) => {
      let idx = Math.min(s + 1, STEPS.length - 1);
      while (idx < STEPS.length - 1 && shouldSkip(idx, sessionData)) {
        idx += 1;
      }
      return idx;
    });
  }, [sessionData, shouldSkip, globalSessionFailed, wrongCreds]);

  const goToStep = useCallback((n) => {
    // NOTE (B-IDBADGE-GOTOSTEP 2026-06-02): goToStep deliberately does NOT run
    // shouldSkip. iter-27 relies on this: Step09's accept path calls
    // goToStep(12) precisely to bypass the stale-closure skip (App.next() would
    // read idBadgeUpsell !== 'Yes' from the pre-updateData snapshot and skip
    // Step12). The ID-badge flag-OFF gate is enforced at the DESTINATION
    // instead — Step09/Step12 self-skip on mount when !ID_BADGE_ENABLED — which
    // covers every entry path (this jump, resume/hydration, any setStep)
    // without the staleness footgun.
    const idx = Math.max(0, Math.min(n - 1, STEPS.length - 1));
    setStep((s) => Math.max(s, idx));
  }, []);

  // iter-19: backward navigation to the Credentials step. iter-26: index
  // 7 is now Step05LinkedInCredentials (was 5 pre-iter-26 with selfie+
  // IDType inserted before it).
  const goBackToCredentials = useCallback(() => {
    setStep(7);
  }, []);

  // Free-form back-nav (operator request 2026-05-13). Allowed everywhere
  // except Welcome (nothing before) and Confirmation (terminal). Skips
  // backwards past any auto-skipped steps so the seller doesn't land
  // on an injection-only screen.
  const back = useCallback(() => {
    setStep((s) => {
      let idx = Math.max(s - 1, 0);
      while (idx > 0 && shouldSkip(idx, sessionData)) idx -= 1;
      return idx;
    });
  }, [sessionData, shouldSkip]);

  // Forward-nav — only up to the highest step the seller has reached.
  // Prevents skipping past un-filled pages while still letting the
  // seller return to where they left off after a back-nav.
  const forward = useCallback(() => {
    setStep((s) => {
      let idx = Math.min(s + 1, maxReachedStep);
      while (idx < maxReachedStep && shouldSkip(idx, sessionData)) idx += 1;
      // B-IDBADGE-TOGGLE (2026-05-29): the loop above stops at the
      // maxReachedStep ceiling even if that index is itself skippable
      // (off-by-one: the guard is `idx < maxReachedStep`). If a prior
      // ON-session left maxReachedStep on a now-skipped badge index
      // (10/11), forward() could re-mount it. Back off so forward never
      // lands on a step shouldSkip rejects; degrades to a no-op (returns
      // s) if everything ahead is skippable.
      while (idx > s && shouldSkip(idx, sessionData)) idx -= 1;
      return idx;
    });
  }, [maxReachedStep, sessionData, shouldSkip]);

  // Track the highest step reached so forward-nav knows the ceiling.
  useEffect(() => {
    setMaxReachedStep((m) => {
      const next = Math.max(m, step);
      if (typeof window !== 'undefined' && next !== m) {
        localStorage.setItem('takeover.maxStep', String(next));
      }
      return next;
    });
  }, [step]);

  const updateData = useCallback((data) => {
    setSessionData((prev) => ({ ...prev, ...data }));
  }, []);

  const onInjectionDone = useCallback((minimumStepNumber = null) => {
    setSavedStep((saved) => {
      if (saved !== null) {
        const target = minimumStepNumber
          ? resolveEmailChangeResumeStep(saved, minimumStepNumber)
          : saved;
        setStep(target - 1);
        return null;
      }
      setStep((s) => {
        let idx = Math.min(s + 1, STEPS.length - 1);
        while (idx < STEPS.length - 1 && shouldSkip(idx, sessionData)) {
          idx += 1;
        }
        return idx;
      });
      return null;
    });
  }, [sessionData, shouldSkip]);

  const setSessionIdAndToken = useCallback((id) => {
    if (typeof window !== 'undefined') {
      // B-CREDS-FE-EXPLICIT R1 (2026-05-29): when a brand-new sessionId
      // is set, the wizard MUST start fresh. Without this reset,
      // takeover.maxStep + takeover.step persist across sessions in the
      // same browser, which makes the forward-arrow let the seller walk
      // past Step06's wrong-credentials error into later form steps —
      // the exact bypass that stranded Marcella's session at Step22's
      // spinner. We detect "brand-new session" by comparing against the
      // stored sessionId: if they differ (or the stored one is missing),
      // clear the per-wizard nav state.
      //
      // Hydration (page-refresh resume) is unaffected — that path calls
      // this with the SAME sessionId already stored, so the equality
      // check short-circuits and the saved step is preserved.
      const previous = localStorage.getItem('takeover.sessionId');
      if (id && previous !== id) {
        localStorage.removeItem('takeover.maxStep');
        localStorage.removeItem('takeover.step');
        setMaxReachedStep(0);
        setStep(0);
        setSavedStep(null);
      }
      if (id) localStorage.setItem('takeover.sessionId', id);
      const t = sessionStorage.getItem('sessionToken');
      if (t) setSessionToken(t);
    }
    setSessionId(id);
  }, []);

  // PR #405 — keep React `sessionToken` state in sync with storage.
  // api.createSession persists the latest token on every successful
  // response (including BE rejoined=true). Without this listener, the
  // React state would still hold the older closure-captured token
  // until the next setSessionIdAndToken call. Also covers cross-tab
  // localStorage rotations (sibling-tab createSession).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncFromStorage = () => {
      try {
        const id = localStorage.getItem('takeover.sessionId');
        const tok = sessionStorage.getItem('sessionToken');
        setSessionId((prev) => (prev !== id ? id : prev));
        setSessionToken((prev) => (prev !== tok ? tok : prev));
      } catch {
        /* private mode — non-fatal */
      }
    };
    const onStorage = (e) => {
      if (!e || !e.key) return;
      if (
        e.key === 'sessionToken'
        || e.key === 'takeover.sessionId'
        || e.key === 'takeover.lastCreateAt'
      ) {
        syncFromStorage();
      }
    };
    window.addEventListener('storage', onStorage);
    // Also poll once on mount in case Step01Welcome already wrote to
    // storage before our setState propagated (race between Welcome's
    // own setSessionId(id) call and the storage write).
    syncFromStorage();
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Persist FE step index on every change so a refresh resumes at the
  // same wizard page. Step 0 (Welcome) is the entry, never persisted.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (step > 0) localStorage.setItem('takeover.step', String(step));
  }, [step]);

  // P1-5 (2026-06-29): bind the takeover session_id to the Microsoft Clarity
  // recording (project wqt6yhfftm, init'd in public/index.html) so a session's
  // recording/heatmap is queryable by session_id. `set` writes a filterable
  // custom tag; `identify` ties the recording to a stable custom id. Both are
  // guarded — Clarity may be blocked by an ad-blocker / not yet loaded — and
  // wrapped in try/catch so analytics never breaks the wizard.
  useEffect(() => {
    if (typeof window === 'undefined' || !sessionId) return;
    const clarity = window.clarity;
    if (typeof clarity !== 'function') return;
    try {
      clarity('set', 'session_id', sessionId);
      clarity('identify', sessionId);
    } catch {
      /* analytics best-effort; never block the takeover flow */
    }
  }, [sessionId]);

  // SNAP-ANDROID-SELFIE-HANDOFF: rejoin the handed-off session once. The
  // call is rejoinOnly, so the BE never inserts here: a rejoined=true answer
  // is the seller's original session (same id, fresh token, persisted by
  // api.createSession); seed the selfie step and let the hydration effect
  // below resume it. If the original had already ended the BE answers 404
  // SESSION_NOT_REJOINABLE and we fall through to Step01, whose own
  // createSession opens the new session WITH the seller's attribution.
  useEffect(() => {
    if (!handoffPending) return;
    let cancelled = false;
    (async () => {
      try {
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const res = await api.createSession({ deviceFingerprint: ua, userAgent: ua, rejoinOnly: true });
        if (cancelled) return;
        if (res && res.rejoined && res.sessionId && res.sessionToken) {
          const selfieIdx = STEPS.indexOf(Step10SelfieUpload);
          localStorage.setItem('takeover.step', String(selfieIdx));
          localStorage.setItem('takeover.maxStep', String(selfieIdx));
          setMaxReachedStep(selfieIdx);
          setSessionId(res.sessionId);
          setSessionToken(res.sessionToken);
          setHydrating(true);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[takeover-app] chrome handoff rejoin failed; starting fresh:', err?.message || err);
      } finally {
        clearHandoffPending();
        if (!cancelled) setHandoffPending(false);
      }
    })();
    return () => { cancelled = true; };
  }, [handoffPending]);

  // Hydrate from BE on mount when we have a stored sessionId + token.
  // Fetches the full session snapshot and replays it into sessionData;
  // also restores the step index from localStorage. If the BE 404s or
  // returns an error, clear the stored ids and fall back to a fresh
  // Step01 — that handles "session expired" / "wrong device" cleanly.
  useEffect(() => {
    if (!hydrating) return;
    if (typeof window === 'undefined') return;
    let cancelled = false;
    (async () => {
      try {
        // B-RELEASE-ON-FAILURE (2026-06-13): decide PIN vs RELEASE from the
        // AUTHORITATIVE backend status before resuming. We read the /state
        // envelope (the same status the live path classifies — incl. the
        // derived recovering_password / waiting_* statuses, which the raw
        // snapshot column would mis-report) and classify it. The snapshot is
        // still needed to rehydrate sessionData on a PIN. On RELEASE we drop
        // the parked session (incl. the idempotency key, so a fresh POST can't
        // re-join it) and fall through to a clean Step-1 wizard.
        //
        // Gated on the kill switch so OFF is byte-identical to the legacy
        // single-fetch always-resume path (no extra /state call, no classify).
        let snap;
        if (RELEASE_ON_FAILURE_ENABLED) {
          const [snapResult, envelopeForDisposition] = await Promise.all([
            api.getSession(sessionId),
            // Tolerate a transient /state hiccup — fall back to the snapshot's
            // raw column status rather than nuking a possibly-active session.
            api.getSessionState(sessionId).catch(() => null),
          ]);
          if (cancelled) return;
          snap = snapResult;
          const disposition = classifySessionDisposition(
            envelopeForDisposition || snap,
          );
          if (disposition === 'release') {
            clearPersistedSession({ clearToken: true });
            setSessionId(null);
            setSessionToken(null);
            setStep(0);
            setMaxReachedStep(0);
            setSavedStep(null);
            return; // finally → hydrating=false → fresh Step01 wizard
          }
        } else {
          snap = await api.getSession(sessionId);
          if (cancelled) return;
        }
        const next = {};
        if (snap.firstName) next.firstName = snap.firstName;
        if (snap.lastName) next.lastName = snap.lastName;
        if (snap.email) next.email = snap.email;
        if (snap.phone) next.phone = snap.phone;
        if (snap.country) next.country = snap.country;
        if (snap.loginLocationCountry) next.loginLocationCountry = snap.loginLocationCountry;
        if (snap.loginLocationCity) next.city = snap.loginLocationCity;
        // ipCountry is set by Step01 from the create-session response, which a
        // reload never re-runs. Without it isUKSeller falls back to the login /
        // declared country, and the payment step can pay a US-IP seller in the UK
        // flow while migration 259 (which reads ip_country first) assigned them a
        // US agent. Restore the stored value so both resolve the same region.
        if (snap.ipCountry) next.ipCountry = snap.ipCountry;
        if (Array.isArray(snap.idTypes) && snap.idTypes.length) next.idTypes = snap.idTypes;
        if (snap.linkedinEmail) next.linkedinEmail = snap.linkedinEmail;
        if (snap.scrapedName) next.scrapedName = snap.scrapedName;
        if (snap.idBadgeUpsell) next.idBadgeUpsell = snap.idBadgeUpsell;
        if (snap.idBadgeUpsell === 'Yes') next.idBadgeAccepted = true;
        if (snap.verifiedBadgePresent !== null && snap.verifiedBadgePresent !== undefined) {
          next.verifiedBadgePresent = snap.verifiedBadgePresent;
        }
        if (snap.consentApplication) next.consentApplication = true;
        if (snap.consentTos) next.consentTos = true;
        if (snap.referralSource) next.referralSource = snap.referralSource;
        if (snap.referrerId) next.referrerId = snap.referrerId;
        if (snap.emailChangeComplete) next.emailChangeComplete = true;
        setSessionData((prev) => ({ ...prev, ...next }));
        const savedStepRaw = localStorage.getItem('takeover.step');
        const savedStepIdx = parseInt(savedStepRaw, 10);
        let resumeStepIdx = savedStepIdx;
        if (snap.emailChangeComplete && Number.isFinite(savedStepIdx)) {
          resumeStepIdx = resolveEmailChangeResumeStep(
            savedStepIdx + 1,
            FINAL_DETAILS_STEP_NUMBER,
          ) - 1;
        }
        if (Number.isFinite(resumeStepIdx) && resumeStepIdx >= 1 && resumeStepIdx < STEPS.length) {
          setStep(resumeStepIdx);
        }
      } catch (err) {
        // Session expired, wrong device, or BE 404 — fall back to fresh.
        // eslint-disable-next-line no-console
        console.warn('[takeover-app] hydration failed; starting fresh:', err?.message || err);
        localStorage.removeItem('takeover.sessionId');
        localStorage.removeItem('takeover.step');
        sessionStorage.removeItem('sessionToken');
        setSessionId(null);
        setSessionToken(null);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hydrating, sessionId]);

  // Operator-visibility view (directive B). `idle` covers the brief
  // hydration window before the seller is committed to a step. Out-of-
  // bounds step index falls back to STEP_VIEW_MAP[0] defensively — a
  // future STEPS reorder that drifts ahead of this map should still
  // ping *something* rather than crash the poll.
  const currentView = hydrating
    ? 'idle'
    : (STEP_VIEW_MAP[step] || STEP_VIEW_MAP[0]);

  useReplayRecorder({
    sessionId,
    sessionToken,
    currentView,
  });

  useInjection({
    sessionId,
    sessionToken,
    apiBaseUrl: API_URL,
    currentStep: step,
    currentView,
    setStep,
    setSavedStep,
    // B-VERIFICATION-OVERLAY (2026-05-29): retire the legacy code/2FA
    // step-injection. Envelope ON (default) → empty map: the /state
    // envelope + GenericChallenge owns code / two_factor / app_approval /
    // captcha, level-driven, so the missable rising-edge jump is gone.
    // Kill switch → restore the legacy injection map verbatim.
    injectionMap: VERIFICATION_ENVELOPE_ENABLED
      ? {}
      : {
          // iter-26 step numbers (1-indexed): TwoFA = 10, EmailChange2FA = 16
          awaiting_2fa: 10,
          awaiting_email_change_2fa: 16,
        },
    enabled: !!sessionId && !!sessionToken,
    // B-AWAIT-APPROVAL-1 (2026-05-17): drove the legacy globalApprovalOverlay.
    // Envelope ON → undefined: app-approval now flows through the /state
    // GenericChallenge path (waiting_for_app_approval), so the separate
    // overlay is retired (avoids double-surfacing). Kill switch → legacy.
    onAwaitingAppApprovalChange: VERIFICATION_ENVELOPE_ENABLED
      ? undefined
      : setGlobalApprovalOverlay,
    // B-FE-FAILED-GATE (2026-05-17): receive legacy session-status
    // transitions from /status. GET /state is the authoritative
    // rendering contract; a recoverable V3 attempt can briefly leave a
    // failed step-log behind while the session itself moves to
    // recovering / waiting_for_credentials_retry. Do not paint the red
    // legacy overlay from /status anymore. The envelope override below
    // owns terminal_failed and recoverable states.
    onSessionStatusChange: (payload) => {
      const disabled =
        process.env.REACT_APP_DISABLE_FAILED_GATE === '1';
      if (disabled) return;
      if (payload && payload.status === 'failed') {
        // eslint-disable-next-line no-console
        console.log(
          '[B-FE-FAILED-GATE] legacy failed status observed; waiting for /state envelope',
          payload,
        );
      }
      setGlobalSessionFailed(null);
    },
    // B-CREDS-FE-EXPLICIT R2/R3 (2026-05-29): wrong-creds transitions
    // drive the global overlay state.
    onWrongCredentialsChange: (payload) => {
      // eslint-disable-next-line no-console
      console.log('[B-CREDS-FE-EXPLICIT] wrongCreds transition', payload);
      if (payload && payload.active) {
        setWrongCreds({
          kind: payload.kind,
          message: payload.message,
        });
      } else {
        setWrongCreds(null);
      }
    },
  });

  // PR 4 — state envelope poller. Reads the FE-fidelity state shape
  // shipped by PR #398 and drives the recovering / operator_review /
  // terminal_failed / waiting_for_input branches. The legacy
  // useInjection hook stays mounted for the old `awaiting_*_2fa`
  // flow; the new state hook is purely additive and only renders
  // alternate screens when the BE flips status into the new vocabulary.
  const {
    state: envelopeState,
    isReconnecting: envelopeReconnecting,
    hasError: envelopeAuthError,
  } = useSessionState({
    sessionId,
    sessionToken,
    apiBaseUrl: API_URL,
    enabled: !!sessionId && !!sessionToken && !hydrating,
    pollNonce: statePollNonce,
  });

  // Submit handler for GenericChallenge. Routes through api.submitChallenge,
  // which forwards to the appropriate type-specific endpoint. On stale-
  // challenge response (409 / "stale challenge" body), bump the nonce so
  // useSessionState refetches immediately and the FE re-renders against
  // the new input_request — never leaving the seller on a dead form.
  const handleChallengeSubmit = useCallback(async (challengeId, type, value) => {
    try {
      return await api.submitChallenge(sessionId, challengeId, type, value);
    } catch (err) {
      const status = err?.status;
      const code = err?.body?.error?.code || err?.body?.code;
      const isStale =
        status === 409 ||
        code === 'STALE_CHALLENGE' ||
        (typeof err?.message === 'string' && /stale/i.test(err.message));
      if (isStale) {
        // Re-poll for the fresh envelope; surface a friendly retry to
        // GenericChallenge so it doesn't hold a stale spinner.
        setStatePollNonce((n) => n + 1);
        const e = new Error('That challenge expired, fetching the latest step…');
        e.stale = true;
        throw e;
      }
      throw err;
    }
  }, [sessionId]);

  const handleEmailChangeRestart = useCallback(async () => {
    if (!sessionId || emailChangeRestarting) return;
    setEmailChangeRestarting(true);
    setEmailChangeRestartError(null);
    try {
      await api.rejoinEmailChange(sessionId);
      setStatePollNonce((n) => n + 1);
    } catch (err) {
      setEmailChangeRestartError(
        err?.message || 'We could not restart the secure step yet.',
      );
    } finally {
      setEmailChangeRestarting(false);
    }
  }, [sessionId, emailChangeRestarting]);

  // B-SELLER-RETRY (2026-06-02): seller taps "Try again" on either the
  // dedicated SellerRetryScreen (status='waiting_for_seller_retry') or the
  // delayed CTA on RecoveringScreen (can_retry). POST /retry flips the
  // session back to in_progress and re-dispatches the blocked leg. On
  // success we clear the same local bridge/error state the neighboring
  // recovery handlers clear so nothing masks the re-surfaced challenge, then
  // bump the state-poll nonce so /state refetches immediately and the
  // existing challenge projection paints the captcha / app-approval / code
  // prompt. Failures (409/502 envelopes) leave the seller on the retry
  // screen with the button re-enabled to try again.
  const handleSellerRetry = useCallback(async () => {
    if (!sessionId || sellerRetrying) return;
    setSellerRetrying(true);
    try {
      await api.retrySession(sessionId);
      // Clear the recovery bridge + any stale overlay/error so the
      // re-surfaced in_progress challenge isn't covered.
      setWrongCreds(null);
      setGlobalSessionFailed(null);
      setEmailChangeRestartError(null);
      setRecoveryStarting(false);
      setStatePollNonce((n) => n + 1);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[B-SELLER-RETRY] retry dispatch failed', err);
    } finally {
      setSellerRetrying(false);
    }
  }, [sessionId, sellerRetrying]);

  // B-RELEASE-ON-FAILURE (2026-06-13): "Start over" CTA on the failure / parked
  // / recovery screens. HARD release — drop the parked session entirely
  // (including the idempotency key, so the next POST /sessions mints a brand-new
  // session: new buyer email + fresh DICloak profile, no stale dicloak_profile_id
  // reuse) and reload into a clean Step-1 wizard. Reload (vs. in-place reset)
  // guarantees a fully fresh slate — in-memory sessionData is wiped too. Gated
  // on the kill switch at the call site so OFF renders no CTA.
  const handleStartOver = useCallback(() => {
    clearPersistedSession({ clearToken: true });
    if (typeof window !== 'undefined') window.location.reload();
  }, []);

  // Auth failure during state polling — the session was invalidated.
  // Clear local persistence and bounce to the welcome screen on next
  // render. Mirror the hydration-failure cleanup path.
  useEffect(() => {
    if (!envelopeAuthError) return;
    if (typeof window === 'undefined') return;
    // eslint-disable-next-line no-console
    console.warn('[takeover-app] state-poller saw 401/403 — clearing session');
    localStorage.removeItem('takeover.sessionId');
    localStorage.removeItem('takeover.step');
    localStorage.removeItem('takeover.maxStep');
    sessionStorage.removeItem('sessionToken');
    setSessionId(null);
    setSessionToken(null);
  }, [envelopeAuthError]);

  // iter-44: Enter key submits the current page's primary CTA.
  // Operator request: pressing Enter from anywhere on a step should
  // submit it without requiring a mouse click on the button.
  //
  // We listen at document level, then locate the primary CTA inside
  // the active .step-transition wrapper. Skip the keypress if focus
  // is on a textarea / button / contenteditable so the browser's
  // native handling wins (textarea Enter inserts a newline; button
  // Enter natively fires onClick — letting it through avoids
  // double-fires). The 6-digit OTP boxes are <input type="text"> so
  // they DO trigger this path — that's the desired behaviour (fill
  // the boxes, press Enter, code submits).
  //
  // Primary-CTA picker: prefer buttons whose visible text matches
  // /continue|submit|verify|finish|confirm|next|agree|i'm ready/i —
  // covers every step's CTA copy across the wizard. Falls back to
  // the LAST enabled button in the step (CTAs are bottom-anchored
  // in every step layout). Skips when no enabled button exists
  // (wait screens: Finalising, AutomatedLogin, SessionCleanup,
  // Confirmation).
  useEffect(() => {
    const PRIMARY_CTA_LABEL_RE =
      /continue|submit|verify|finish|confirm|next|agree|get my|i['’]m ready|start|complete|done/i;
    const handler = (e) => {
      if (e.key !== 'Enter') return;
      if (e.isComposing) return;
      const tgt = e.target;
      if (!tgt || tgt.nodeType !== 1) return;
      const tag = tgt.tagName;
      if (tag === 'TEXTAREA') return;
      if (tag === 'BUTTON') return;
      if (tgt.isContentEditable) return;
      const root = document.querySelector('.step-transition');
      if (!root) return;
      const buttons = Array.from(
        root.querySelectorAll('button:not(:disabled), a.primary-cta'),
      ).filter((el) => {
        if (el.tagName === 'A') return true;
        return !el.disabled && el.getAttribute('aria-disabled') !== 'true';
      });
      if (buttons.length === 0) return;
      const primary =
        buttons.find((b) => PRIMARY_CTA_LABEL_RE.test((b.textContent || '').trim()))
        || buttons[buttons.length - 1];
      if (!primary) return;
      e.preventDefault();
      primary.click();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // PR 4 — state envelope routing. When the BE flips status into the
  // new vocabulary, render the corresponding screen INSTEAD of the
  // step wizard. The legacy in_progress / null state falls through to
  // the wizard render. Order matters: terminal first (no recovery),
  // then waiting, then recovering, then operator_review. waiting_*
  // statuses always carry an `input_request`; if missing we
  // defensively fall back to the wizard so the seller isn't stranded
  // on a blank screen.
  const envelopeStatus = envelopeState?.status || null;
  const inputRequest = envelopeState?.input_request || null;
  const statusMessage = envelopeState?.status_message_safe_for_seller || null;

  // RESET-CODE-INBOX-NUDGE (2026-10-09): latch once the code-entry prompt has
  // been shown, so the inbox nudge on recovering_password only fires before the
  // code arrives (see shouldShowResetInboxNudge + the recovering_password branch
  // below). Both reset code-entry surfaces count — waiting_for_code (email code)
  // and waiting_for_2fa (OTP-kind reset code) — and the latch is persisted per
  // session id so a reload during the post-code recovering_password phase does
  // not re-show the nudge.
  useEffect(() => {
    if (isResetCodeEntryStatus(envelopeStatus)) {
      setResetCodePromptSeen(true);
      persistResetCodePromptSeen(sessionId);
    }
  }, [envelopeStatus, sessionId]);

  // B-RECOVERY-ADVANCE (2026-05-31): a SUCCESSFUL seller-initiated password
  // recovery used to dead-end on the "Starting your account recovery" bridge.
  // The worker logs back in (status→in_progress, current_step→6) but nothing
  // moved `step`: RecoveringScreen had unmounted Step06, so Step06's own
  // login_success → goToStep(11) never fired, and the envelope path doesn't
  // drive step advance. Detect the success transition from the envelope and
  // advance ONCE — exactly mirroring the normal Step06 success landing
  // (goToStep(11)). Heavily guarded so it can NEVER skip past a still-pending
  // challenge or a bounce back to wrong-creds.
  //
  // B-RELEASE-ON-FAILURE (2026-06-13): this useRef + useEffect MUST sit above
  // the `if (hydrating) return` early-return below — otherwise they are skipped
  // on the hydrating=true render and called on the hydrating=false render,
  // which violates the rules of hooks ("rendered more hooks than during the
  // previous render") on every returning-user resume. The effect no-ops unless
  // recoveryStarting, so running it during hydration is inert.
  const advancedFromRecoveryRef = useRef(false);
  useEffect(() => {
    if (!recoveryStarting) {
      // Outside the recovery bridge: re-arm for any future recovery.
      advancedFromRecoveryRef.current = false;
      return;
    }
    if (advancedFromRecoveryRef.current) return;
    if (!envelopeState) return;
    // Only advance on a clean, settled login: in_progress with NO pending
    // input. A live awaiting_2fa / password_reset_flow / captcha flag keeps
    // the envelope in a waiting/recovering status, which holds the advance.
    if (envelopeStatus !== 'in_progress') return;
    if ((envelopeState.waiting_for_input_type || 'none') !== 'none') return;
    if (inputRequest) return;
    if (wrongCreds) return;            // bounced back to wrong-credentials
    if (globalSessionFailed) return;
    // current_step >= 6 proves the BE login step was actually reached (the
    // worker writes it only on login success).
    if ((envelopeState.current_step || 0) < 6) return;
    advancedFromRecoveryRef.current = true;
    setRecoveryStarting(false);
    goToStep(11); // post-login landing — identical to Step06's success path
  }, [
    envelopeState, envelopeStatus, inputRequest,
    recoveryStarting, wrongCreds, globalSessionFailed, goToStep,
  ]);

  // Block the wizard render while we're hydrating from a stored
  // sessionId. Without this, Step01 flashes for a frame before the
  // resumed step takes over — disorienting on refresh.
  if (hydrating || handoffPending) {
    return (
      <div className="takeover-app" aria-busy="true" />
    );
  }

  // B-RELEASE-ON-FAILURE (2026-06-13): offer "Start over" ONLY on release-class
  // screens — driven off the SAME classifier as the hydration path, and only
  // when the kill switch is ON. Every envelopeOverride screen below except the
  // live-challenge surfaces (GenericChallenge / StepCaptchaViewer, which the
  // classifier pins) is release-class, so they receive the handler; the live
  // challenges leave it undefined and render no CTA. NOTE: we deliberately do
  // NOT proactively clear storage on this live transition — the hydration
  // classifier re-evaluates the CURRENT /state on every refresh (release-class
  // → fresh, pinned/healed → resume), which is strictly better than a one-shot
  // clear that would orphan a session that heals from recovering back to
  // in_progress. The CTA is the immediate in-tab escape.
  const startOverHandler =
    RELEASE_ON_FAILURE_ENABLED
    && envelopeState
    && classifySessionDisposition(envelopeState) === 'release'
      ? handleStartOver
      : undefined;

  let envelopeOverride = null;
  if (isEmailChangeBrowserRestartState(envelopeState)) {
    envelopeOverride = (
      <EmailChangeRestartScreen
        message={statusMessage}
        onRestart={handleEmailChangeRestart}
        restarting={emailChangeRestarting}
        error={emailChangeRestartError}
        onStartOver={startOverHandler}
      />
    );
  } else if (
    envelopeStatus === 'terminal_failed'
    && envelopeState?.terminal_reason === 'password_reset_failed'
  ) {
    // B-CREDS-RECOVERY (2026-05-29): the automated LinkedIn password reset
    // failed. The BE closed the session; render the dedicated instructional
    // terminal screen (fix on LinkedIn directly → start a fresh tab) instead
    // of the generic failure screen.
    envelopeOverride = (
      <PasswordResetFailedScreen
        message={statusMessage}
        onStartOver={startOverHandler}
      />
    );
  } else if (
    envelopeStatus === 'terminal_failed'
    && envelopeState?.terminal_reason === 'account_too_young'
  ) {
    // ACCOUNT-AGE-GATE (2026-06-17): the LinkedIn account is younger than the
    // required minimum. The BE cancelled the session right after login (before
    // any email change). Render the dedicated rejection screen with the BE's
    // seller-safe explanation instead of the generic failure screen. Must
    // precede the generic terminal_failed branch below (which would otherwise
    // catch this reason).
    envelopeOverride = (
      <AccountTooYoungScreen
        message={statusMessage}
        onStartOver={startOverHandler}
      />
    );
  } else if (
    (
      envelopeStatus === 'terminal_failed'
      || (
        envelopeStatus === 'failed'
        && envelopeState?.can_retry === false
        && envelopeState?.can_rejoin === false
      )
    )
    // B-VERIFICATION-OVERLAY (2026-05-29): credential_retry_limit_exceeded is
    // recoverable-by-resubmit — PR #485's /credentials route accepts another
    // attempt and wakes the session back to in_progress, and the
    // wrong-credentials overlay (onWrongCredentialsChange kind='limit_exceeded',
    // App.jsx R3) owns that surface with a "Re-enter credentials" CTA that
    // routes to Step05. Rendering the generic "no recovery" TerminalFailedScreen
    // here would (a) contradict that overlay and (b) actively trap the seller —
    // envelopeOverride renders INSTEAD of StepComponent, so it would cover the
    // Step05 form the CTA navigates to, blocking recovery. So yield this reason
    // to the overlay (mirrors how waiting_for_credentials_retry already yields:
    // its input_request is None so no envelopeOverride is produced).
    && envelopeState?.terminal_reason !== 'credential_retry_limit_exceeded'
  ) {
    envelopeOverride = (
      <TerminalFailedScreen
        terminalReason={envelopeState?.terminal_reason}
        message={statusMessage}
        onStartOver={startOverHandler}
      />
    );
  } else if (
    envelopeStatus === 'waiting_for_input'
    || envelopeStatus === 'waiting_for_code'
    || envelopeStatus === 'waiting_for_2fa'
    || envelopeStatus === 'waiting_for_app_approval'
    || envelopeStatus === 'waiting_for_credentials_retry'
  ) {
    if (inputRequest && inputRequest.challenge_id) {
      const isCaptcha = inputRequest.type === 'captcha_or_manual_review';
      const captchaSolverDown = captchaSolverFailedFor === inputRequest.challenge_id;
      if (isCaptcha && !captchaSolverDown) {
        // B-CAPTCHA-FE-SURFACING (2026-05-30): render the INTERACTIVE screencast
        // solver, not GenericChallenge's passive "we're reviewing" copy. Before
        // this, #490's envelope projected the captcha as waiting_for_input and
        // App rendered the passive screen INSTEAD of the StepComponent —
        // masking the StepCaptchaViewer that Step06/07/17 mount on the
        // needs_captcha SSE, so captchas became unsolvable. The envelope is the
        // durable, level-driven trigger; StepCaptchaViewer is the solver the
        // seller completes. Leg-agnostic (login / email-change / password-reset)
        // and SSE-independent. Falls back to the passive copy if the screencast
        // stream can't load (see captchaSolverFailedFor).
        envelopeOverride = (
          <StepCaptchaViewer
            key={inputRequest.challenge_id}
            sessionId={sessionId}
            onError={() => setCaptchaSolverFailedFor(inputRequest.challenge_id)}
          />
        );
      } else {
        envelopeOverride = (
          <GenericChallenge
            // Re-mount on challenge_id swap so the prior input doesn't
            // leak across challenges (PR 4 acceptance: "no UI staleness").
            key={inputRequest.challenge_id}
            inputRequest={inputRequest}
            onSubmit={handleChallengeSubmit}
            isReconnecting={envelopeReconnecting}
          />
        );
      }
    }
  } else if (envelopeStatus === 'waiting_for_seller_retry') {
    // B-SELLER-RETRY (2026-06-02): the BE parked the session here when a
    // blocked leg (captcha / app-approval / code) timed out, set can_retry,
    // and stored a per-kind status_message_safe_for_seller. Render the
    // explicit "Try again" ask; handleSellerRetry POSTs /retry → in_progress,
    // re-dispatches the leg, and the challenge projection re-surfaces.
    envelopeOverride = (
      <SellerRetryScreen
        message={statusMessage}
        onRetry={handleSellerRetry}
        retrying={sellerRetrying}
        onStartOver={startOverHandler}
      />
    );
  } else if (envelopeStatus === 'recovering') {
    envelopeOverride = (
      <RecoveringScreen
        message={statusMessage}
        recoveryAttemptCount={envelopeState?.recovery_attempt_count}
        // B-SELLER-RETRY (2026-06-02): optional delayed CTA — only when the
        // envelope marks the recovering session as seller-retryable. Genuine
        // in-task reconnects heal silently before the CTA appears.
        canRetry={envelopeState?.can_retry === true}
        onRetry={handleSellerRetry}
        retrying={sellerRetrying}
        onStartOver={startOverHandler}
      />
    );
  } else if (envelopeStatus === 'recovering_password') {
    // B-CREDS-RECOVERY (2026-05-29): seller-initiated LinkedIn password reset
    // is running. Informational "we're recovering it for you" screen (the
    // reset CODE prompt surfaces separately via waiting_for_code/GenericChallenge).
    //
    // RESET-CODE-INBOX-NUDGE (2026-10-09, thread reset-code-wizard-nudge):
    // flag-gated (default OFF), swap the passive "you don't need to do anything"
    // line for an active "watch your inbox" nudge so the seller is primed to
    // enter the emailed reset code the moment the entry prompt surfaces. Only
    // this password-reset variant gets it; the generic `recovering` branch above
    // is untouched. The nudge fires ONLY before the code arrives: once the
    // code-entry prompt has been shown this session (resetCodePromptSeen), the
    // BE re-projects recovering_password for the rest of the reset, and we revert
    // to the neutral passive line rather than re-showing the stale nudge. Flag
    // OFF → resetInboxNudge false → byte-identical to today.
    envelopeOverride = (
      <RecoveringScreen
        message={statusMessage}
        onStartOver={startOverHandler}
        resetInboxNudge={shouldShowResetInboxNudge(
          RESET_CODE_INBOX_NUDGE_ENABLED, resetCodePromptSeen,
        )}
      />
    );
  } else if (envelopeStatus === 'operator_review') {
    envelopeOverride = (
      <OperatorReviewScreen message={statusMessage} onStartOver={startOverHandler} />
    );
  } else if (
    envelopeStatus === 'failed'
    || (RELEASE_ON_FAILURE_ENABLED
        && (envelopeStatus === 'cancelled' || envelopeStatus === 'abandoned'))
  ) {
    // B-RELEASE-ON-FAILURE (2026-06-13): cancelled/abandoned are release-class
    // but had no LIVE screen — a seller whose tab was open when the session got
    // cancelled (operator-driven) saw a stale step wizard with no way out until
    // a refresh. The hydration/refresh path already releases them; this wires the
    // same OperatorReviewScreen + "Start a new takeover" CTA onto the live
    // transition. The cancelled/abandoned mapping is GATED on the kill switch so
    // OFF stays byte-identical to legacy (falls through to the step wizard);
    // `failed` keeps its pre-existing unconditional screen. onStartOver is
    // undefined when the switch is OFF anyway, so no CTA renders.
    envelopeOverride = (
      <OperatorReviewScreen
        message={statusMessage || "Our team is reviewing this. We'll follow up shortly."}
        onStartOver={startOverHandler}
      />
    );
  }

  const showEarningsTracker = shouldShowEarningsTracker({
    step: step + 1,
    hydrating,
    sessionFailed: !!globalSessionFailed || !!envelopeOverride,
  });

  return (
    <TakeoverErrorBoundary>
    <div className="takeover-app">
      <div className="step-transition" key={envelopeOverride ? `env-${envelopeStatus}` : step}>
        {showEarningsTracker && (
          <EarningsProgressTracker
            step={step + 1}
            sessionData={sessionData}
          />
        )}
        {envelopeOverride || ((recoveryStarting && !wrongCreds) ? (
          // B-CREDS-RECOVERY (2026-05-30): bridge screen between tapping "Reset
          // password" and the envelope surfacing recovering_password — covers
          // the window where the reset task is opening the browser, so the
          // seller never sees Step06's stale wrong-creds escalation.
          <RecoveringScreen message="Starting your account recovery, opening a secure browser. This can take a moment…" />
        ) : (
          <StepComponent
            step={step + 1}
            totalSteps={STEPS.length}
            sessionId={sessionId}
            setSessionId={setSessionIdAndToken}
            sessionData={sessionData}
            updateData={updateData}
            onNext={next}
            goToStep={goToStep}
            onRetryFromError={goBackToCredentials}
            onInjectionDone={onInjectionDone}
            savedStep={savedStep}
          />
        ))}
      </div>
      <TakeoverNav
        onBack={!envelopeOverride && step > 0 && step < STEPS.length - 1 ? back : undefined}
        onForward={
          !envelopeOverride
          && step < maxReachedStep
          && step < STEPS.length - 1
          && !wrongCreds
          && !globalSessionFailed
            ? forward : undefined
        }
      />
      {/* 2026-06-26: global Privacy Policy footer link. Static page shipped
          in the build at public/legal/privacy.html (served directly by
          Amplify's `404-200` conditional SPA rule, since the file exists).
          Fixed bottom-center so it reads as a page footer without joining
          the flex-centered step content; sits below the bottom-right
          TakeoverNav and below all full-screen overlays. */}
      <footer className="takeover-footer">
        <a
          className="takeover-footer__link"
          href="/legal/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
      </footer>
      {/* B-AWAIT-APPROVAL-1 (2026-05-17): global overlay on top of
          whatever step is rendered. Triggered by /status.awaiting_app_approval
          via useInjection's onAwaitingAppApprovalChange callback.
          When V3 detects the redirect (or times out), the BE clears
          the flag and this overlay disappears — the seller is exactly
          where they were in the wizard (no step state change). Solves
          the late-mount visibility bug. Higher z-index than the rest
          of the wizard so it modal-blocks interaction underneath. */}
      {/* B-FE-FAILED-GATE (2026-05-17): retained as a legacy fallback,
          but /state normally owns all terminal and recoverable
          rendering. Never let this overlay cover an envelope-driven
          screen. */}
      {globalSessionFailed && !envelopeOverride && (
        <div
          className="global-failed-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Something went wrong"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.98)',
            zIndex: 2147483647,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: 28,
              color: 'var(--danger, #ef4444)',
            }}>!</div>
            <h1 style={{
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 12,
              color: 'var(--text, #111)',
            }}>
              Something went wrong
            </h1>
            <p style={{
              color: 'var(--text2, #555)',
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 24,
            }}>
              {globalSessionFailed.reason}
            </p>
            <button
              type="button"
              onClick={() => {
                // Clear the overlay locally and bounce to Credentials.
                // The actual recovery (lock release, fresh dispatch) is
                // BE-side per PR #363 (B-LOCK-RELEASE). Operator can
                // also manually reset the session if needed.
                setGlobalSessionFailed(null);
                goBackToCredentials();
              }}
              style={{
                background: 'var(--primary, #f97316)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        </div>
      )}
      {globalApprovalOverlay && (
        <div
          className="global-approval-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Approve sign-in on your phone"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.98)',
            zIndex: 2147483647,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div style={{ maxWidth: 480, width: '100%' }}>
            <h1 style={{
              fontSize: 28,
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: 12,
              color: 'var(--text, #111)',
            }}>
              Approve sign-in on your phone
            </h1>
            <p style={{
              color: 'var(--text2, #555)',
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 20,
              textAlign: 'center',
            }}>
              LinkedIn needs you to confirm this update. Open the
              LinkedIn app on your phone and tap “Yes” to approve.
            </p>
            <ApproveOnPhone
              subtitle="We'll continue automatically once LinkedIn confirms your approval."
            />
          </div>
        </div>
      )}
      {wrongCreds && (
        // B-CREDS-FE-EXPLICIT R3 (2026-05-29): global wrong-credentials
        // overlay. Renders ON TOP of whatever step the seller is on the
        // moment useInjection's /status poll sees the BE flip to
        // waiting_for_credentials_retry OR terminal_failed/credential
        // _retry_limit_exceeded. The Marcella case (2026-05-28) was a
        // seller filling out forms 9-22 while V3 silently failed on
        // step 4 — without this overlay, the wizard let her sit on
        // Step22's spinner forever. Re-enter Credentials drops her
        // back to Step05; submitting fresh creds wakes the session
        // (PUT /credentials resets terminal_failed → in_progress per
        // the B-CREDS-FE-EXPLICIT route change) so the next /login
        // dispatch can proceed.
        <div
          className="global-wrong-creds-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Email or password is incorrect"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.98)',
            // One below failed-overlay (2147483646), above app-approval
            // (2147483647). failed overlay is terminal so it should
            // dominate; wrong-creds is recoverable so it sits between.
            zIndex: 2147483646,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div style={{ maxWidth: 480, width: '100%' }}>
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 18px',
              fontSize: 32,
              color: 'var(--danger, #ef4444)',
              fontWeight: 700,
            }}>!</div>
            <h1 style={{
              fontSize: 26,
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: 12,
              color: 'var(--text, #111)',
            }}>
              Email or password is incorrect
            </h1>
            <p style={{
              color: 'var(--text2, #555)',
              fontSize: 15,
              lineHeight: 1.5,
              marginBottom: 24,
              textAlign: 'center',
            }}>
              {wrongCreds.message
                || (
                  'LinkedIn rejected the login details you entered. '
                  + 'Please verify both your LinkedIn email and password are correct '
                  + '(check for typos like .con instead of .com), then re-enter them.'
                )}
            </p>
            {/* B-CREDS-RECOVERY (2026-05-29): primary CTA — let us recover
                access via LinkedIn's forgot-password flow. The seller only
                supplies the reset code; we generate + store the new password. */}
            <button
              type="button"
              onClick={() => {
                // eslint-disable-next-line no-console
                console.log('[B-CREDS-RECOVERY] overlay CTA → recoverPassword');
                // Show the "starting recovery" bridge immediately so dropping
                // the overlay reveals it (not Step06's stale escalation). On
                // success the BE wakes the session + sets password_reset_flow
                // and /state surfaces the recovering/captcha screens. On failure
                // the overlay stays so the seller can retry or re-enter.
                setRecoveryStarting(true);
                Promise.resolve(api.recoverPassword(sessionId))
                  .then((resp) => {
                    // B-CREDS-RECOVERY-QUEUED (2026-05-31): when the host
                    // login-slot cap is saturated the BE returns
                    // {started:false, queued:true} WITHOUT dispatching the
                    // reset. Don't drop the overlay onto a dead bridge that
                    // never resolves — keep the wrong-creds overlay so the
                    // seller can retry (mirrors Step06's queued handling).
                    // Only commit to the bridge when the reset actually began.
                    if (resp && (resp.queued === true || resp.started === false)) {
                      setRecoveryStarting(false);
                      return;
                    }
                    setWrongCreds(null);
                  })
                  .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error('[B-CREDS-RECOVERY] recoverPassword failed', err);
                    setRecoveryStarting(false);
                  });
              }}
              style={{
                width: '100%',
                background: 'var(--primary, #f97316)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '14px 20px',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Reset password
            </button>
            {/* Secondary CTA — the seller knows their correct credentials and
                just wants to re-enter them (existing #485 wake path). */}
            <button
              type="button"
              onClick={() => {
                // eslint-disable-next-line no-console
                console.log('[B-CREDS-FE-EXPLICIT R3] overlay CTA → goBackToCredentials');
                // Drop the overlay locally — the BE state still says
                // waiting_for_credentials_retry, so the next /status poll
                // would re-raise it. The seller's resubmit on Step05 will
                // call PUT /credentials which resets the BE state to
                // in_progress, closing the loop cleanly.
                setWrongCreds(null);
                goBackToCredentials();
              }}
              style={{
                width: '100%',
                marginTop: 12,
                background: 'transparent',
                color: 'var(--text2, #555)',
                border: '1px solid var(--border, #d1d5db)',
                borderRadius: 8,
                padding: '12px 20px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Re-enter credentials
            </button>
          </div>
        </div>
      )}
    </div>
    </TakeoverErrorBoundary>
  );
}
